import type { ReactNode } from 'react'
import cx from 'classnames'
import { Icon, type IconName } from '@/components/Icon'
import { match } from '@/utils/pattern-match-util'
import styles from './EmptyState.module.css'
import type { FittedIllustration } from '@/components/SpotIllustration/fitted-illustration'

export interface EmptyStateProps {
  children?: ReactNode
  /**
   * `section` fits inside a card or form section. `card` is the classic
   * design's compact take on `section`, for job details cards.
   */
  size?: 'base' | 'sm' | 'section' | 'card'
  /**
   * Adds space above a `card` empty state.
   */
  padTop?: boolean
  /**
   * Adds generous vertical padding, for page-level placeholders.
   */
  pad?: boolean
  narrow?: boolean
  /**
   * Fills the remaining space of a flex parent.
   */
  grow?: boolean
  testId?: string
}

/**
 * Placeholder for a page or list with nothing to show.
 */
export function EmptyState({
  children,
  size = 'base',
  pad,
  padTop,
  narrow,
  grow,
  testId,
}: EmptyStateProps) {
  return (
    <div
      className={cx(
        styles.root,
        size === 'card' && styles.cardRoot,
        grow && styles.grow,
      )}
      data-test={testId}
    >
      <div
        className={cx(
          styles.content,
          match(size, {
            base: undefined,
            sm: styles.sm,
            section: styles.section,
            card: cx(styles.section, styles.card),
          }),
          pad && styles.pad,
          padTop && styles.padTop,
          narrow && styles.narrow,
        )}
      >
        {children}
      </div>
    </div>
  )
}

interface EmptyStateTitleProps {
  children?: ReactNode
  testId?: string
}

/**
 * The headline of an {@link EmptyState}.
 */
export function EmptyStateTitle({ children, testId }: EmptyStateTitleProps) {
  return (
    <h3 className={styles.title} data-test={testId}>
      {children}
    </h3>
  )
}

interface EmptyStateDescriptionProps {
  children?: ReactNode
  testId?: string
}

/**
 * The supporting text of an {@link EmptyState}.
 */
export function EmptyStateDescription({
  children,
  testId,
}: EmptyStateDescriptionProps) {
  return (
    <div className={styles.description} data-test={testId}>
      {children}
    </div>
  )
}

interface EmptyStateActionsProps {
  children?: ReactNode
  testId?: string
}

/**
 * The buttons of an {@link EmptyState}.
 */
export function EmptyStateActions({
  children,
  testId,
}: EmptyStateActionsProps) {
  return (
    <div className={styles.actions} data-test={testId}>
      {children}
    </div>
  )
}

interface EmptyStateDecorationProps {
  children?: ReactNode
}

/**
 * The visual above an {@link EmptyState}'s title.
 */
export function EmptyStateDecoration({ children }: EmptyStateDecorationProps) {
  return <div className={styles.decoration}>{children}</div>
}

interface EmptyStateIconDecorationProps {
  icon: IconName
}

/**
 * An icon as the {@link EmptyState}'s decoration, for placeholders in panes.
 */
export function EmptyStateIconDecoration({
  icon,
}: EmptyStateIconDecorationProps) {
  return (
    <EmptyStateDecoration>
      <span className={styles.icon}>
        <Icon name={icon} size={20} />
      </span>
    </EmptyStateDecoration>
  )
}

interface EmptyStateIllustrationProps {
  src?: string
  /**
   * Shown instead of `src` when set.
   */
  illustration?: FittedIllustration
  alt?: string
  small?: boolean
}

/**
 * An illustration image as the {@link EmptyState}'s decoration.
 */
export function EmptyStateIllustration({
  src,
  illustration,
  alt,
  small,
}: EmptyStateIllustrationProps) {
  const Illustration = illustration
  const className = cx(styles.illustration, small && styles.small)
  return (
    <EmptyStateDecoration>
      {Illustration ? (
        <Illustration className={className} label={alt} />
      ) : (
        <img className={className} src={src} alt={alt} />
      )}
    </EmptyStateDecoration>
  )
}
